import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { APIList } from '../../../../../../../../typings/API.interface';
import { APIDestination } from '../../../../../../../../typings/Destination.interface';
import { IssueType } from '../../../../../../../../typings/Issue.interface';
import { APIIssueTrigger } from '../../../../../../../../typings/IssueTrigger.interface';
import { APISource } from '../../../../../../../../typings/Source.interface';
import { Transformation } from '../../../../../../../../typings/Transformation.interface';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { issue_type_configs } from '../../../../../utils/issues';
import Button from '../../../../common/base/Button';
import { StyledCardSection } from '../../../../common/base/Card';
import Divider from '../../../../common/base/Divider';
import Skeleton from '../../../../common/base/Skeleton';
import { Div } from '../../../../common/helpers/StyledUtils';
import WithLoader from '../../../../common/WithLoader';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import IssueTriggersGroup from './IssueTriggersGroup';
export interface IssueTriggersRenderData {
  transformations_by_id: Record<string, Transformation>;
  webhooks_by_id: Record<string, APIWebhook>;
  destinations_by_id: Record<string, APIDestination>;
  sources_by_id: Record<string, APISource>;
}
const IssueTriggersListSkeleton: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Div>{Object.entries(issue_type_configs).map(_temp)}</Div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const extractResourceIdFromIssueTriggers = (issue_triggers: APIIssueTrigger[], property: 'connections' | 'transformations' | 'destinations' | 'sources'): string[] => {
  return Array.from(new Set(issue_triggers.reduce((arr, issue) => Array.isArray(issue.configs[property]) ? [...arr, ...issue.configs[property]] : arr, [])));
};
const TriggersList: React.FC<{
  type: IssueType;
  status?: ('enabled' | 'disabled')[];
}> = t0 => {
  const $ = _c(43);
  const {
    type,
    status
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["type", "created_at"];
    t2 = ["asc", "asc"];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== status) {
    t3 = status && status.length === 1 ? status[0] === "enabled" ? null : {
      any: true
    } : undefined;
    $[2] = status;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== type) {
    t4 = {
      order_by: t1,
      dir: t2,
      limit: 100,
      type,
      disabled_at: t3
    };
    $[4] = t3;
    $[5] = type;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const filters = t4;
  let t5;
  if ($[7] !== filters) {
    t5 = (_, previous_data) => APIMethodKeys.issue_triggers.list({
      ...filters,
      next: previous_data?.pagination.next
    });
    $[7] = filters;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== HookdeckAPI) {
    t6 = t7 => {
      const [url] = t7;
      return HookdeckAPI.request("GET", url);
    };
    $[9] = HookdeckAPI;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    data: issue_triggers_pages,
    size,
    setSize,
    mutate
  } = useSWRInfinite(t5, t6);
  const issue_triggers = issue_triggers_pages?.reduce(_temp2, []) || [];
  const webhook_ids = issue_triggers ? extractResourceIdFromIssueTriggers(issue_triggers.filter(_temp3), "connections") : [];
  const destination_ids = issue_triggers ? extractResourceIdFromIssueTriggers(issue_triggers.filter(_temp4), "destinations") : [];
  const transformation_ids = issue_triggers ? extractResourceIdFromIssueTriggers(issue_triggers.filter(_temp5), "transformations") : [];
  const source_ids = issue_triggers ? extractResourceIdFromIssueTriggers(issue_triggers.filter(_temp6), "sources") : [];
  const {
    data: webhooks
  } = useSWR(webhook_ids.length > 0 && APIMethodKeys.webhooks.list({
    id: webhook_ids
  }), () => HookdeckAPI.webhooks.list({
    id: webhook_ids
  }));
  const {
    data: destinations
  } = useSWR(destination_ids.length > 0 && APIMethodKeys.destinations.list({
    id: destination_ids
  }), () => HookdeckAPI.destinations.list({
    id: destination_ids
  }));
  const {
    data: transformations
  } = useSWR(transformation_ids.length > 0 && APIMethodKeys.transformations.list({
    id: transformation_ids
  }), () => HookdeckAPI.transformations.list({
    id: transformation_ids
  }));
  let t7;
  if ($[11] !== HookdeckAPI.sources || $[12] !== source_ids) {
    t7 = () => HookdeckAPI.sources.list({
      id: source_ids
    });
    $[11] = HookdeckAPI.sources;
    $[12] = source_ids;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    data: sources
  } = useSWR(source_ids.length > 0 && APIMethodKeys.sources.list({
    id: source_ids
  }), t7);
  let t8;
  if ($[14] !== webhooks?.models) {
    t8 = webhooks?.models.reduce(_temp7, {}) || {};
    $[14] = webhooks?.models;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const webhooks_by_id = t8;
  let t9;
  if ($[16] !== destinations?.models) {
    t9 = destinations?.models.reduce(_temp8, {}) || {};
    $[16] = destinations?.models;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const destinations_by_id = t9;
  let t10;
  if ($[18] !== transformations?.models) {
    t10 = transformations?.models.reduce(_temp9, {}) || {};
    $[18] = transformations?.models;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const transformations_by_id = t10;
  let t11;
  if ($[20] !== sources?.models) {
    t11 = sources?.models.reduce(_temp10, {}) || {};
    $[20] = sources?.models;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const sources_by_id = t11;
  let t12;
  if ($[22] !== destinations_by_id || $[23] !== sources_by_id || $[24] !== transformations_by_id || $[25] !== webhooks_by_id) {
    t12 = {
      webhooks_by_id,
      transformations_by_id,
      destinations_by_id,
      sources_by_id
    };
    $[22] = destinations_by_id;
    $[23] = sources_by_id;
    $[24] = transformations_by_id;
    $[25] = webhooks_by_id;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const render_data = t12;
  const loading = !issue_triggers_pages || webhook_ids.length > 0 && !webhooks || destination_ids.length > 0 && !destinations || transformation_ids.length > 0 && !transformations || source_ids.length > 0 && !sources;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <IssueTriggersListSkeleton />;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== mutate) {
    t14 = () => {
      mutate();
    };
    $[28] = mutate;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== issue_triggers || $[31] !== render_data || $[32] !== t14 || $[33] !== type) {
    t15 = <IssueTriggersGroup onIssueTriggerCreated={t14} issue_triggers={issue_triggers} type={type} render_data={render_data} />;
    $[30] = issue_triggers;
    $[31] = render_data;
    $[32] = t14;
    $[33] = type;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== issue_triggers_pages || $[36] !== setSize || $[37] !== size) {
    t16 = issue_triggers_pages && issue_triggers_pages[issue_triggers_pages.length - 1].pagination.next && <Div flex={{
      justify: "center"
    }} p={2} m={{
      b: 8,
      t: 4
    }}><Button icon="history" onClick={() => setSize(size + 1)} outline={true}>View more triggers</Button></Div>;
    $[35] = issue_triggers_pages;
    $[36] = setSize;
    $[37] = size;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== loading || $[40] !== t15 || $[41] !== t16) {
    t17 = <WithLoader loading={loading} loading_element={t13}>{t15}{t16}</WithLoader>;
    $[39] = loading;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
};
export default TriggersList;
function _temp(t0) {
  const [key] = t0;
  return <Div key={key}><StyledCardSection p={{
      x: 6,
      y: 4
    }}><Skeleton m={{
        r: 2
      }} w={{
        px: 400
      }} h={{
        px: 20
      }} border_radius="normal" /><Skeleton w={{
        px: 70
      }} h={{
        px: 20
      }} border_radius="normal" m={{
        t: 1
      }} /></StyledCardSection><Divider /></Div>;
}
function _temp2(arr, page) {
  return [...arr, ...page.models];
}
function _temp3(t0) {
  const {
    type: type_0
  } = t0;
  return type_0 === "delivery";
}
function _temp4(t0) {
  const {
    type: type_1
  } = t0;
  return type_1 === "backpressure";
}
function _temp5(t0) {
  const {
    type: type_2
  } = t0;
  return type_2 === "transformation";
}
function _temp6(t0) {
  const {
    type: type_3
  } = t0;
  return type_3 === "request";
}
function _temp7(object, webhook) {
  return {
    ...object,
    [webhook.id]: webhook
  };
}
function _temp8(object_0, destination) {
  return {
    ...object_0,
    [destination.id]: destination
  };
}
function _temp9(object_1, transformation) {
  return {
    ...object_1,
    [transformation.id]: transformation
  };
}
function _temp10(object_2, source) {
  return {
    ...object_2,
    [source.id]: source
  };
}